import { useCallback, useMemo } from "react";
import type { Selection } from "../../../../hooks/useSelection";
import type { DataTableSelection } from "./DataTable";

/** Options for useDataTableSelection. */
export interface UseDataTableSelectionOptions<Row> {
  rows: readonly Row[];
  getRowId: (row: Row) => string;
  /** Rows that get a checkbox; every row when omitted. Pass a stable function. */
  isSelectable?: (row: Row) => boolean;
  /** When false the table shows no selection column at all. Default: true. */
  enabled?: boolean;
}

const EVERY_ROW = () => true;

/** Wires a Selection into a table's checkbox column: one checkbox per selectable row, and a
 *  header checkbox over the selectable rows currently shown. Undefined when disabled. */
export function useDataTableSelection<Row>(
  selection: Selection,
  { rows, getRowId, isSelectable = EVERY_ROW, enabled = true }: UseDataTableSelectionOptions<Row>
): DataTableSelection<Row> | undefined {
  const { selectedIds, count, toggle, toggleAll, areAllSelected } = selection;

  const selectableIds = useMemo(
    () => rows.filter(isSelectable).map(getRowId),
    [rows, isSelectable, getRowId]
  );

  const onToggleAll = useCallback(() => toggleAll(selectableIds), [toggleAll, selectableIds]);

  return useMemo(
    () =>
      enabled
        ? {
            selectedIds,
            onToggle: toggle,
            isSelectable,
            onToggleAll,
            allSelected: areAllSelected(selectableIds),
            anySelected: count > 0,
          }
        : undefined,
    [enabled, selectedIds, toggle, isSelectable, onToggleAll, areAllSelected, selectableIds, count]
  );
}
